import restclient from "@/v1/services/restclient";


const OPERATOR_ID_MANDATORY = "operatorID parameter should be defined";

/**
 * getOperatorsByQuery
 * Get list of operators by a query
 * @param {string} query - For example: page, name, sorts, etc.
 * @returns {Promise<object[]>}
 */
export const getOperatorsByQuery = async query => {
  const response = await restclient.get(`/api/v1/operators${query}`);
  return response.data;
};

/**
 * createOperator
 * Creates a new operator
 * @param {object} body
 * @param {string} body.name - The name of the operator
 * @returns {Promise<object>}
 */
export const createOperator = async body => {
  const response = await restclient.post("/api/v1/operators", body);
  return response.data;
};

/**
 * getOperatorByID
 * @param {string} ID - The ID of the operator
 * @returns {Promise}
 */
export const getOperatorByID = async ID => {
  const response = await restclient.get(`/api/v1/operators/${ID}`);
  return response.data;
};

/**
 * editOperatorByID
 * Edits a operator
 * @param {string} ID - the ID of the operator
 * @param {object} body
 * @returns {Promise<object>}
 */
export const editOperatorByID = async (ID, body) => {
  const response = await restclient.patch(`/api/v1/operators/${ID}`, body);
  return response.data;
};

/**
 * getSetupsByOperatorID
 * @param {string} ID - The ID of the operator
 * @returns {Promise}
 */
export const getSetupsByOperatorID = async ID => {
  const response = await restclient.get(`/api/v1/operators/${ID}/integrator-setups`);
  return response.data;
};

/**
 * createSetupForOperator
 * Creates a new setup for an operator
 * @param {string} operatorID - ID of the operator
 * @param {object} body - body of the new setup
 * @returns {Promise}
 */
export const createSetupForOperator = async (operatorID, body) => {
  const response = await restclient.post(`/api/v1/operators/${operatorID}/integrator-setups`, body);
  return response.data;
};

/**
 * getCredentialsByOperatorID
 * @param {string} ID - The ID of the operator
 * @returns {Promise}
 */
export const getCredentialsByOperatorID = async (ID, query) => {
  const response = await restclient.get(`/api/v1/operators/${ID}/credentials${query || ""}`);
  return response.data;
};

/**
 * editCredentialByID
 * Create credential for an specific environment
 * @param {string} operatorID - The ID of the operator
 * @param {string} credentialID - The ID of the credential
 * @param {object} body - The new body of the credential
 * @returns {Promise}
 */
export const editCredentialByID = async (operatorID, credentialID, body) => {
  const response = await restclient.patch(
    `/api/v1/operators/${operatorID}/credentials/${credentialID}`,
    body,
  );
  return response.data;
};

/**
 * createCredential
 * Create credential for an specific environment
 * @param {string} operatorID - The ID of the operator
 * @returns {Promise}
 */
export const createCredential = async (operatorID, body) => {
  const response = await restclient.post(`/api/v1/operators/${operatorID}/credentials`, body);
  return response.data;
};

/**
 * uploadLogo
 * Uploads a logo image for a specific operator
 * @param {string} operatorID - The ID of the operator
 * @param {File} logo - Logo file image
 * @returns {Promise}
 */
export const uploadLogo = async (operatorID, logo) => {
  const formData = new FormData();
  formData.append("file", logo);
  const response = await restclient.post(`/api/v1/operators/${operatorID}/logo`, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
  return response.data;
};

/**
 * getWhitelistedIpsByOperatorID
 * GET whitelisted IPs by ID
 * @param {string} operatorID - ID of the Operator
 * @returns {Promise}
 */
export const getWhitelistedIpsByOperatorID = (operatorID) => {
  if (typeof operatorID === "undefined") {
    throw new Error(OPERATOR_ID_MANDATORY);
  }
  return restclient
    .get(`/api/v1/aws-conf/waf/operators/${operatorID}`)
    .then((response) => response.data);
}

export const setWhitelistedIpsByOperatorID = (operatorID, body) => {
  if (typeof operatorID === "undefined") {
    throw new Error(OPERATOR_ID_MANDATORY);
  }
  const value = body.value;
  if (value.length > 0){
    return restclient.post(`/api/v1/aws-conf/waf/operators/${operatorID}`, value.split(","));
  }
  return restclient.post(`/api/v1/aws-conf/waf/operators/${operatorID}`, []);
}

export const removeWhitelistedIpsByOperatorID = (operatorID) => {
  if (typeof operatorID === "undefined") {
    throw new Error(OPERATOR_ID_MANDATORY);
  }
  return restclient.post(`/api/v1/aws-conf/waf/operators/${operatorID}`, []);
}
